import { DataGrid } from "@mui/x-data-grid";
import { forwardRef, useImperativeHandle } from "react";
import { esES } from "@mui/x-data-grid/locales";
import useDagLogs from "../../../hooks/useDagLogs";
import { buildDagLogFilterParams } from "../../../utils/helpers";

const DAG_STATUS_OPTIONS = [
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "running", label: "Running" },
  { value: "pending", label: "Pending" },
  { value: "skipped", label: "Skipped" },
];

const Dags = forwardRef((_, ref) => {
  const {
    rows,
    rowCount,
    loading,
    pagination,
    setPagination,
    filterModel,
    setFilterModel,
  } = useDagLogs();

  const columns = [
    {
      field: "timestamp",
      headerName: "Fecha y hora",
      flex: 1,
      minWidth: 180,
      type: "dateTime",
      valueFormatter: (params) => {
        const date = new Date(params);
        return new Intl.DateTimeFormat("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }).format(date);
      },
    },
    {
      field: "dag_id",
      headerName: "DAG",
      flex: 1,
      minWidth: 200,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: "task_id",
      headerName: "Tarea",
      flex: 1,
      minWidth: 180,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (value) => value || "-",
    },
    {
      field: "status",
      headerName: "Estado",
      flex: 1,
      minWidth: 140,
      type: "singleSelect",
      valueOptions: DAG_STATUS_OPTIONS,
      valueFormatter: (params) => {
        const status = DAG_STATUS_OPTIONS.find(({ value }) => value === params);
        return status?.label ?? params;
      },
    },
    {
      field: "event_type",
      headerName: "Evento",
      flex: 1,
      minWidth: 180,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: "description",
      headerName: "Descripcion",
      flex: 1,
      minWidth: 300,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (value) => value || "-",
    },
    {
      field: "parameters",
      headerName: "Parametros",
      flex: 1,
      minWidth: 280,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (value) => {
        if (!value) return "-";
        if (typeof value === "string") return value;
        return JSON.stringify(value);
      },
    },
    {
      field: "error_message",
      headerName: "Error",
      flex: 1,
      minWidth: 260,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (value) => value || "-",
    },
  ];

  useImperativeHandle(ref, () => ({
    getExportParams: () => buildDagLogFilterParams(filterModel),
  }));

  return (
    <div className="w-full h-[600px]">
      <DataGrid
        columns={columns}
        rows={rows}
        pagination
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[10, 25, 50, 100]}
        rowCount={rowCount}
        loading={loading}
        getRowHeight={() => "auto"}
        getEstimatedRowHeight={() => 160}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "hsl(240 4.8% 95.9%)",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
          },
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            backgroundColor: "white",
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: "hsl(240 4.8% 95.9%)",
          },
          "& .MuiDataGrid-cell": {
            py: 1,
          },
        }}
      />
    </div>
  );
});

export default Dags;
